import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";

const DivisionesSelector = ({ divisiones, docente, asigna }) => {
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [totalBloques, setTotalBloques] = useState(0);
  const [horasAsignadas, setHorasAsignadas] = useState(0);
  const [fechaConsulta, setFechaConsulta] = useState("");
  const [resultadoConsulta, setResultadoConsulta] = useState(null);

  const materia = divisiones?.Materia?.[0];

  const horasMaximasDocenteCivil = 36;
  const horasMaximasDocenteRetirado = 10;
  const horasMaximasDocenteEnActividad = 12;

  let limiteHoras = 0;

  /* =========================================================
     FUNCIONES PARA HORAS Y AD HONOREM
     ========================================================= */

  const normalizarTexto = (valor) =>
    String(valor ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[_-]/g, " ")
      .toUpperCase()
      .trim();

  const esAsignacionAdHonorem = (asignacion) => {
    if (!asignacion) return false;

    const condicion = normalizarTexto(
      asignacion?.condicion ||
        asignacion?.tipoAsignacion ||
        asignacion?.caracterAsignacion ||
        asignacion?.modalidad ||
        asignacion?.tipo ||
        ""
    );

    const esRentada = normalizarTexto(
      asignacion?.esRentada ?? ""
    );

    const adHonorem = asignacion?.adHonorem;

    return (
      adHonorem === true ||
      adHonorem === 1 ||
      adHonorem === "1" ||
      normalizarTexto(adHonorem) === "SI" ||
      esRentada === "NO" ||
      condicion.includes("AD HONOREM") ||
      condicion.includes("ADHONOREM")
    );
  };

  const convertirHoras = (valor) => {
    if (
      valor === null ||
      valor === undefined ||
      valor === "" ||
      valor === "-" ||
      valor === "--"
    ) {
      return 0;
    }

    const texto = String(valor)
      .trim()
      .replace(",", ".")
      .replace(/[^\d.-]/g, "");

    if (
      !texto ||
      texto === "-" ||
      texto === "." ||
      texto === "-."
    ) {
      return 0;
    }

    const numero = Number(texto);

    return Number.isFinite(numero) ? numero : 0;
  };

  const obtenerHorasContabilizables = (asignacion) => {
    if (esAsignacionAdHonorem(asignacion)) {
      return 0;
    }

    return convertirHoras(asignacion?.horasAsignadas);
  };

  const asignacionesAdHonorem = useMemo(() => {
    return (
      docente?.asignacionMaterias?.filter(
        esAsignacionAdHonorem
      ) || []
    );
  }, [docente]);

  const cantidadAdHonorem = asignacionesAdHonorem.length;

  /* =========================================================
     VALIDAR TIPO DE DOCENTE
     ========================================================= */

  useEffect(() => {
    if (!docente?.docenteTipo) {
      Swal.fire(
        "Atención",
        "El docente seleccionado no posee tipo, por lo que el máximo de horas no puede ser calculado",
        "warning"
      );

      asigna?.(null);
    }
  }, [docente?.docenteTipo, asigna]);

  if (docente?.docenteTipo === "Policia Actividad") {
    limiteHoras = horasMaximasDocenteEnActividad;
  } else if (
    docente?.docenteTipo === "Policia Retirado"
  ) {
    limiteHoras = horasMaximasDocenteRetirado;
  } else if (
    docente?.docenteTipo === "Personal Civil"
  ) {
    limiteHoras = horasMaximasDocenteCivil;
  }

  /* =========================================================
     CALCULAR HORAS YA ASIGNADAS
     SOLO RENTADAS
     ========================================================= */

  useEffect(() => {
    const horasTotales =
      docente?.asignacionMaterias?.reduce(
        (acumulado, asignacion) => {
          return (
            acumulado +
            obtenerHorasContabilizables(asignacion)
          );
        },
        0
      ) || 0;

    setHorasAsignadas(
      Number.isFinite(horasTotales)
        ? horasTotales
        : 0
    );
  }, [docente]);

  /* =========================================================
     FECHAS
     ========================================================= */

  const convertirAFechaLocal = (fechaStr) => {
    if (!fechaStr) return null;

    const partes = String(fechaStr)
      .slice(0, 10)
      .split("-")
      .map(Number);

    if (partes.length !== 3) return null;

    const [year, month, day] = partes;

    if (!year || !month || !day) return null;

    const fecha = new Date(
      year,
      month - 1,
      day
    );

    if (Number.isNaN(fecha.getTime())) {
      return null;
    }

    fecha.setHours(0, 0, 0, 0);

    return fecha;
  };

  const fechaAInput = (fecha) => {
    if (!fecha) return "";

    const year = fecha.getFullYear();
    const month = String(
      fecha.getMonth() + 1
    ).padStart(2, "0");
    const day = String(
      fecha.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const obtenerFechaInicioSugerida = () => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const inicioMateria =
      convertirAFechaLocal(materia?.fechaInicio);

    const finMateria =
      convertirAFechaLocal(
        materia?.fechaFinalizacion
      );

    if (!inicioMateria || !finMateria) {
      return materia?.fechaInicio || "";
    }

    if (
      hoy >= inicioMateria &&
      hoy <= finMateria
    ) {
      return fechaAInput(hoy);
    }

    if (hoy < inicioMateria) {
      return fechaAInput(inicioMateria);
    }

    return fechaAInput(finMateria);
  };

  /* =========================================================
     CALCULAR EXCESO EN TODO EL PERÍODO
     AHORA DESDE EL INICIO REAL DEL CARGO
     ========================================================= */

  const calcularExcesoHoras = (
    divisionesSeleccionadas = seleccionadas,
    fechaInicioAsignacion
  ) => {
    if (!materia) {
      return {
        excede: false,
        detalle: [],
      };
    }

    const fechaInicioNueva =
      convertirAFechaLocal(
        fechaInicioAsignacion ||
          materia.fechaInicio
      );

    const fechaFinNueva =
      convertirAFechaLocal(
        materia.fechaFinalizacion
      );

    if (
      !fechaInicioNueva ||
      !fechaFinNueva
    ) {
      return {
        excede: false,
        detalle: [],
      };
    }

    const detalle = [];

    let fechaCursor = new Date(
      fechaInicioNueva
    );

    while (fechaCursor <= fechaFinNueva) {
      let horasEseDia = 0;

      docente?.asignacionMaterias?.forEach(
        (asignacion) => {
          if (
            esAsignacionAdHonorem(asignacion)
          ) {
            return;
          }

          const inicio =
            convertirAFechaLocal(
              asignacion?.fechaInicio
            );

          const fin =
            convertirAFechaLocal(
              asignacion?.fechaFinalizacion
            );

          if (!inicio || !fin) return;

          if (
            inicio <= fechaCursor &&
            fechaCursor <= fin
          ) {
            horasEseDia +=
              obtenerHorasContabilizables(
                asignacion
              );
          }
        }
      );

      const horasPorDivision =
        convertirHoras(
          materia?.bloqueHorario
        );

      const horasProyectadas =
        divisionesSeleccionadas.length *
        horasPorDivision;

      const totalProyectado =
        horasEseDia + horasProyectadas;

      if (totalProyectado > limiteHoras) {
        detalle.push({
          dia: fechaCursor.toLocaleDateString(
            "es-AR"
          ),

          horasActuales: horasEseDia,
          horasProyectadas,
          totalProyectado,
          limiteHoras,

          exceso:
            totalProyectado - limiteHoras,
        });
      }

      fechaCursor.setDate(
        fechaCursor.getDate() + 1
      );
    }

    return {
      excede: detalle.length > 0,
      detalle,
    };
  };

  /* =========================================================
     SELECCIONAR DIVISIONES
     ========================================================= */

  const toggleSeleccion = (numero) => {
    setSeleccionadas((prev) => {
      const nuevasSeleccionadas =
        prev.includes(numero)
          ? prev.filter(
              (item) => item !== numero
            )
          : [...prev, numero];

      const horasPorDivision =
        convertirHoras(
          materia?.bloqueHorario
        );

      setTotalBloques(
        nuevasSeleccionadas.length *
          horasPorDivision
      );

      return nuevasSeleccionadas;
    });
  };

  /* =========================================================
     CONSULTAR FECHA ESPECÍFICA
     ========================================================= */

  const verificarFechaEspecifica = (
    fechaStr
  ) => {
    if (!materia) return;

    const fecha =
      convertirAFechaLocal(fechaStr);

    if (!fecha) {
      setResultadoConsulta(null);
      return;
    }

    let horasEseDia = 0;

    docente?.asignacionMaterias?.forEach(
      (asignacion) => {
        if (
          esAsignacionAdHonorem(asignacion)
        ) {
          return;
        }

        const inicio =
          convertirAFechaLocal(
            asignacion?.fechaInicio
          );

        const fin =
          convertirAFechaLocal(
            asignacion?.fechaFinalizacion
          );

        if (!inicio || !fin) return;

        if (
          inicio <= fecha &&
          fecha <= fin
        ) {
          horasEseDia +=
            obtenerHorasContabilizables(
              asignacion
            );
        }
      }
    );

    const horasPorDivision =
      convertirHoras(
        materia?.bloqueHorario
      );

    const horasProyectadas =
      seleccionadas.length *
      horasPorDivision;

    const totalProyectado =
      horasEseDia + horasProyectadas;

    const excede =
      totalProyectado > limiteHoras;

    setResultadoConsulta({
      dia: fecha.toLocaleDateString(
        "es-AR"
      ),

      horasActuales: horasEseDia,
      horasProyectadas,
      totalProyectado,
      excede,

      diferencia: Math.abs(
        limiteHoras - totalProyectado
      ),
    });
  };

  /* =========================================================
     ASIGNAR
     ========================================================= */

  const manejarAsignar = async () => {
    if (!materia) return;

    if (seleccionadas.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Seleccione divisiones",
        text: "Debe seleccionar al menos una división.",
      });

      return;
    }

    /* =======================================================
       1. TIPO DE ASIGNACIÓN
       ======================================================= */

    const tipoAsignacion = await Swal.fire({
      title: "Tipo de asignación",

      html: `
        <p>Seleccione cómo desea cargar esta asignación.</p>

        <p>
          <b>Rentada:</b>
          suma horas al límite del docente.
        </p>

        <p>
          <b>Ad Honorem:</b>
          se guarda con 0 horas y no se contabiliza.
        </p>
      `,

      icon: "question",
      showDenyButton: true,
      showCancelButton: true,

      confirmButtonText: "💰 Rentada",
      denyButtonText: "🤝 Ad Honorem",
      cancelButtonText: "Cancelar",

      reverseButtons: true,
    });

    if (tipoAsignacion.isDismissed) {
      return;
    }

    const esAdHonorem =
      tipoAsignacion.isDenied;

    const horasQueSeAsignan =
      esAdHonorem ? 0 : totalBloques;

    /* =======================================================
       2. FECHA REAL DE INICIO DEL CARGO
       ======================================================= */

    const fechaInicioMateria =
      String(
        materia?.fechaInicio || ""
      ).slice(0, 10);

    const fechaFinMateria =
      String(
        materia?.fechaFinalizacion || ""
      ).slice(0, 10);

    const fechaInicioSugerida =
      obtenerFechaInicioSugerida();

    const resultadoFecha =
      await Swal.fire({
        title: "Inicio de la asignación",

        html: `
          <div style="text-align:left;">
            <p>
              Indique desde qué fecha el docente
              toma efectivamente el cargo.
            </p>

            <p>
              <b>Inicio de la materia:</b>
              ${fechaInicioMateria}
            </p>

            <p>
              <b>Finalización de la materia:</b>
              ${fechaFinMateria}
            </p>

            <p style="margin-bottom:6px;">
              <b>Inicio del cargo:</b>
            </p>

            <input
              id="fechaInicioAsignacion"
              type="date"
              class="swal2-input"
              value="${fechaInicioSugerida}"
              min="${fechaInicioMateria}"
              max="${fechaFinMateria}"
              style="
                width:90%;
                margin:0 auto;
                display:block;
              "
            />
          </div>
        `,

        icon: "question",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
        confirmButtonText: "Continuar",

        preConfirm: () => {
          const input =
            document.getElementById(
              "fechaInicioAsignacion"
            );

          const valor = input?.value;

          if (!valor) {
            Swal.showValidationMessage(
              "Debe seleccionar la fecha de inicio del cargo."
            );

            return false;
          }

          const fechaSeleccionada =
            convertirAFechaLocal(valor);

          const inicio =
            convertirAFechaLocal(
              fechaInicioMateria
            );

          const fin =
            convertirAFechaLocal(
              fechaFinMateria
            );

          if (
            !fechaSeleccionada ||
            !inicio ||
            !fin
          ) {
            Swal.showValidationMessage(
              "La fecha seleccionada no es válida."
            );

            return false;
          }

          if (
            fechaSeleccionada < inicio
          ) {
            Swal.showValidationMessage(
              "La asignación no puede comenzar antes del inicio de la materia."
            );

            return false;
          }

          if (
            fechaSeleccionada > fin
          ) {
            Swal.showValidationMessage(
              "La asignación no puede comenzar después de la finalización de la materia."
            );

            return false;
          }

          return valor;
        },
      });

    if (!resultadoFecha.isConfirmed) {
      return;
    }

    const fechaInicioAsignacion =
      resultadoFecha.value;

    const fechaFinalizacionAsignacion =
      fechaFinMateria;

    console.log(
      "Fecha inicio real del cargo:",
      fechaInicioAsignacion
    );

    console.log(
      "Fecha finalización asignación:",
      fechaFinalizacionAsignacion
    );

    /* =======================================================
       3. CONTROL DE EXCESO
       DESDE LA FECHA REAL DEL CARGO
       ======================================================= */

    if (!esAdHonorem) {
      const exceso =
        calcularExcesoHoras(
          seleccionadas,
          fechaInicioAsignacion
        );

      if (exceso.excede) {
        const detalleHtml =
          exceso.detalle
            .slice(0, 5)
            .map(
              (detalle) => `
                <div
                  style="
                    text-align:left;
                    margin-bottom:10px;
                    padding:8px;
                    border-radius:8px;
                    background:#fff3f3;
                    border:1px solid #f0caca;
                  "
                >
                  <b>${detalle.dia}</b><br/>

                  Horas rentadas actuales:
                  ${detalle.horasActuales}<br/>

                  Horas proyectadas:
                  ${detalle.horasProyectadas}<br/>

                  Total proyectado:
                  ${detalle.totalProyectado}<br/>

                  Límite:
                  ${detalle.limiteHoras}<br/>

                  <b>
                    Exceso:
                    ${detalle.exceso}
                  </b>
                </div>
              `
            )
            .join("");

        await Swal.fire({
          title: "❌ Exceso de horas",

          html: `
            <p>
              La asignación rentada supera el
              límite permitido desde la fecha
              real de inicio del cargo.
            </p>

            <p>
              <b>Inicio del cargo:</b>
              ${fechaInicioAsignacion}
            </p>

            <p>
              Las asignaciones Ad Honorem existentes
              no fueron contabilizadas.
            </p>

            ${detalleHtml}

            ${
              exceso.detalle.length > 5
                ? `
                  <p>
                    <b>
                      Y ${
                        exceso.detalle.length -
                        5
                      } día/s más con exceso.
                    </b>
                  </p>
                `
                : ""
            }

            <hr/>

            <p>
              Si corresponde, vuelva a intentar
              y seleccione
              <b>Ad Honorem</b>.
            </p>
          `,

          icon: "error",
        });

        return;
      }
    }

    /* =======================================================
       4. SEDE
       ======================================================= */

    const resultadoSede =
      await Swal.fire({
        title: "Sede de cursada",

        input: "select",

        inputOptions: {
          [materia.sedeCursada]:
            materia.sedeCursada,
        },

        inputValue:
          materia.sedeCursada,

        inputAttributes: {
          disabled: true,
        },

        confirmButtonText: "Continuar",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
      });

    if (!resultadoSede.isConfirmed) {
      return;
    }

    const sede =
      resultadoSede.value ||
      materia.sedeCursada;

    if (!sede) return;

    /* =======================================================
       5. CONFIRMACIÓN FINAL
       ======================================================= */

    const confirmacion =
      await Swal.fire({
        title: "¿Confirmar asignación?",

        html: `
          <div style="text-align:left;">
            <p>
              <strong>Materia:</strong>
              ${materia.nombreMateria}
            </p>

            <p>
              <strong>Docente:</strong>
              ${docente.docenteApellido},
              ${docente.docenteNombres}
            </p>

            <p>
              <strong>Divisiones:</strong>
              ${seleccionadas.join(", ")}
            </p>

            <p>
              <strong>Inicio del cargo:</strong>
              ${fechaInicioAsignacion}
            </p>

            <p>
              <strong>Finalización:</strong>
              ${fechaFinalizacionAsignacion}
            </p>

            <p>
              <strong>
                Horas reales seleccionadas:
              </strong>
              ${totalBloques}
            </p>

            <p>
              <strong>
                Horas que se guardan:
              </strong>
              ${horasQueSeAsignan}
            </p>

            <p>
              <strong>Es rentada:</strong>
              ${esAdHonorem ? "NO" : "SI"}
            </p>

            <p>
              <strong>Condición:</strong>
              ${
                esAdHonorem
                  ? "AD HONOREM"
                  : "RENTADA"
              }
            </p>

            <p>
              <strong>Sede:</strong>
              ${materia.sedeCursada}
            </p>
          </div>
        `,

        icon: "question",
        showCancelButton: true,

        cancelButtonText: "Cancelar",
        confirmButtonText: "Confirmar",
      });

    if (!confirmacion.isConfirmed) {
      return;
    }

    /* =======================================================
       6. DEVOLVER ASIGNACIÓN
       ======================================================= */

    asigna?.({
      divisiones: seleccionadas,

      materia:
        materia.nombreMateria,

      docente: `${docente.docenteApellido}, ${docente.docenteNombres}`,

      totalHoras:
        horasQueSeAsignan,

      horasRealesSeleccionadas:
        totalBloques,

      adHonorem:
        esAdHonorem,

      esRentada:
        esAdHonorem ? "NO" : "SI",

      condicion:
        esAdHonorem
          ? "AD HONOREM"
          : "RENTADA",

      sedeCursada:
        materia.sedeCursada,

      fechaInicio:
        fechaInicioAsignacion,

      fechaFinalizacion:
        fechaFinalizacionAsignacion,
    });

    await Swal.fire(
      "✅ Asignado",

      esAdHonorem
        ? `La asignación fue confirmada como Ad Honorem desde el ${fechaInicioAsignacion}, con 0 horas y no será contabilizada.`
        : `La asignación rentada fue confirmada desde el ${fechaInicioAsignacion}.`,

      "success"
    );
  };

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div>
      {materia && (
        <>
          <h5>
            Asignar Divisiones a{" "}
            {docente.docenteApellido +
              ", " +
              docente.docenteNombres}{" "}
            de la materia{" "}
            {materia.nombreMateria}
          </h5>

          <p>
            Curso: {materia.curso}
          </p>

          <p>
            Escuela: {materia.escuela}
          </p>

          <p>
            Período de la Materia:{" "}
            <strong>
              {materia.fechaInicio}
            </strong>{" "}
            al{" "}
            <strong>
              {materia.fechaFinalizacion}
            </strong>
          </p>

          <p>
            Horas por División de la Materia:{" "}
            {convertirHoras(
              materia.bloqueHorario
            )}
          </p>

          <p>
            Horas rentadas ya asignadas al
            docente:{" "}
            <strong>
              {horasAsignadas}
            </strong>
          </p>

          {cantidadAdHonorem > 0 && (
            <p
              style={{
                padding: "8px 10px",
                borderRadius: "8px",
                backgroundColor: "#eaf7f0",
                border:
                  "1px solid #c5e6d4",
                color: "#286144",
              }}
            >
              Asignaciones Ad Honorem no
              contabilizadas:{" "}
              <strong>
                {cantidadAdHonorem}
              </strong>
            </p>
          )}

          <p>
            Total de Horas Seleccionadas:{" "}
            <strong>
              {totalBloques}
            </strong>
          </p>

          <p>
            Límite de Horas Diarias
            Simultáneas:{" "}
            <strong>
              {limiteHoras}
            </strong>
          </p>

          <hr />

          <h3
            style={{
              textAlign: "center",
            }}
          >
            Divisiones Libres
          </h3>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "5px",
            }}
          >
            {(divisiones?.Libres || []).map(
              (numero) => {
                const seleccionada =
                  seleccionadas.includes(
                    numero
                  );

                return (
                  <button
                    type="button"
                    key={numero}
                    onClick={() =>
                      toggleSeleccion(numero)
                    }
                    style={{
                      backgroundColor:
                        seleccionada
                          ? "#16804f"
                          : "#718096",

                      color: "white",
                      margin: "5px",
                      borderRadius: "50%",
                      border:
                        seleccionada
                          ? "3px solid #0b5332"
                          : "3px solid #4a5568",

                      height: "50px",
                      width: "50px",

                      cursor: "pointer",
                      fontWeight: 800,
                    }}
                  >
                    {numero}
                  </button>
                );
              }
            )}
          </div>

          <hr />

          <h3
            style={{
              textAlign: "center",
            }}
          >
            Divisiones Ocupadas
          </h3>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "5px",
            }}
          >
            {(divisiones?.Ocupadas || []).map(
              (numero) => (
                <button
                  type="button"
                  key={numero}
                  style={{
                    backgroundColor:
                      "#c53030",

                    color: "white",
                    margin: "5px",
                    borderRadius: "50%",
                    border:
                      "3px solid #822727",

                    height: "50px",
                    width: "50px",

                    cursor: "not-allowed",
                    opacity: 0.75,
                    fontWeight: 800,
                  }}
                  disabled
                >
                  {numero}
                </button>
              )
            )}
          </div>

          <div
            style={{
              textAlign: "center",
            }}
          >
            <button
              type="button"
              onClick={manejarAsignar}
              disabled={
                seleccionadas.length === 0
              }
              style={{
                marginTop: "20px",
                padding: "10px 18px",

                border: "none",
                borderRadius: "8px",

                backgroundColor:
                  seleccionadas.length === 0
                    ? "#a0aec0"
                    : "#315f9c",

                color: "white",

                cursor:
                  seleccionadas.length === 0
                    ? "not-allowed"
                    : "pointer",

                fontWeight: 800,
              }}
            >
              Asignar Divisiones
            </button>
          </div>

          <hr />

          <div
            style={{
              textAlign: "center",
            }}
          >
            <h3>
              Consultar disponibilidad en un
              día específico
            </h3>

            <input
              type="date"
              value={fechaConsulta}
              onChange={(event) => {
                const valor =
                  event.target.value;

                setFechaConsulta(valor);

                verificarFechaEspecifica(
                  valor
                );
              }}
              style={{
                padding: "8px 10px",
                border:
                  "1px solid #cbd5e0",
                borderRadius: "8px",
              }}
            />

            {resultadoConsulta && (
              <div
                style={{
                  maxWidth: "420px",
                  margin: "15px auto 0",
                  padding: "14px",

                  borderRadius: "10px",

                  backgroundColor:
                    resultadoConsulta.excede
                      ? "#fff1f1"
                      : "#edf9f2",

                  border:
                    resultadoConsulta.excede
                      ? "1px solid #f1c2c2"
                      : "1px solid #bce3cc",
                }}
              >
                <p>
                  📅 Día:{" "}
                  {resultadoConsulta.dia}
                </p>

                <p>
                  Horas rentadas actuales:{" "}
                  {
                    resultadoConsulta.horasActuales
                  }
                </p>

                <p>
                  Horas proyectadas:{" "}
                  {
                    resultadoConsulta.horasProyectadas
                  }
                </p>

                <p>
                  Total proyectado:{" "}
                  {
                    resultadoConsulta.totalProyectado
                  }
                </p>

                {cantidadAdHonorem > 0 && (
                  <p
                    style={{
                      color: "#286144",
                      fontSize: "13px",
                    }}
                  >
                    Las asignaciones Ad Honorem
                    existentes no fueron
                    contabilizadas.
                  </p>
                )}

                <p
                  style={{
                    color:
                      resultadoConsulta.excede
                        ? "red"
                        : "green",

                    fontWeight: 800,
                  }}
                >
                  {resultadoConsulta.excede
                    ? `❌ Se excede por ${resultadoConsulta.diferencia} horas. Puede cargarse como Ad Honorem.`
                    : `✅ Le quedan ${resultadoConsulta.diferencia} horas disponibles para ese día.`}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DivisionesSelector;
